/**
 * RunHistory — Audit trail of every agent run. MOCKED.
 */
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import MarketingShell from "@/components/MarketingShell";
import { History, Bot, CheckCircle2, XCircle, Clock, ChevronRight, Sparkles } from "lucide-react";

const RUNS = [];

const STATUS_TAG = { completed: "tag-green", awaiting_approval: "tag-amber", failed: "tag-red", running: "tag-blue" };
const STATUS_ICON = { completed: CheckCircle2, awaiting_approval: Clock, failed: XCircle, running: Sparkles };

export default function RunHistory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState(searchParams.get("filter") || "all");
  useEffect(() => {
    const urlFilter = searchParams.get("filter");
    if (urlFilter && urlFilter !== filter) setFilter(urlFilter);
  }, [searchParams, filter]);
  const setActiveFilter = (next) => {
    setFilter(next);
    const sp = new URLSearchParams(searchParams);
    if (next === "all") sp.delete("filter"); else sp.set("filter", next);
    setSearchParams(sp, { replace: true });
  };
  const filtered = filter === "all" ? RUNS : RUNS.filter((r) => r.status === filter);

  return (
    <MarketingShell title="Run History" subtitle="Audit trail of every agent run · last 7 days">
      <div data-testid="run-history-page">
        <div className="mkt-segmented" style={{ marginBottom: 16 }}>
          {[
            { k: "all", l: "All" },
            { k: "completed", l: "Completed" },
            { k: "awaiting_approval", l: "Awaiting approval" },
            { k: "failed", l: "Failed" },
          ].map((f) => (
            <button key={f.k} onClick={() => setActiveFilter(f.k)} className={filter === f.k ? "active" : ""} data-testid={`run-filter-${f.k}`}>
              {f.l}
            </button>
          ))}
        </div>

        <div className="mkt-table-wrap">
          <table className="mkt-table">
            <thead>
              <tr>{["Run ID", "Agent", "Trigger", "Input", "Status", "Duration", "Tokens", "Artifacts", "Time", ""].map(h => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const SIcon = STATUS_ICON[r.status] || CheckCircle2;
                return (
                  <tr key={r.id} data-testid={`run-${r.id}`}>
                    <td style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--blue)" }}>{r.id}</td>
                    <td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12 }}>
                        <Bot style={{ width: 12, height: 12, color: "var(--blue)" }} /> {r.agent}
                      </span>
                    </td>
                    <td style={{ fontSize: 11.5, color: "var(--text-tertiary)", textTransform: "capitalize" }}>{r.trigger.replace("_", " ")}</td>
                    <td style={{ fontSize: 12, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.input}</td>
                    <td>
                      <span className={`tag ${STATUS_TAG[r.status]}`} style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                        <SIcon style={{ width: 10, height: 10 }} /> {r.status.replace("_", " ")}
                      </span>
                    </td>
                    <td className="num" style={{ fontSize: 11.5, color: "var(--text-tertiary)" }}>{(r.duration / 1000).toFixed(1)}s</td>
                    <td className="num" style={{ fontSize: 11.5, color: "var(--text-tertiary)" }}>{r.tokens.toLocaleString()}</td>
                    <td className="num" style={{ fontSize: 11.5, color: "var(--text-tertiary)" }}>{r.artifacts}</td>
                    <td style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{r.time}</td>
                    <td>
                      <button className="btn" style={{ height: 24, padding: "0 6px" }}>
                        <ChevronRight style={{ width: 12, height: 12 }} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div data-testid="runs-empty" style={{ padding: 40, textAlign: "center", color: "var(--text-tertiary)", fontSize: 12.5 }}>
              No agent runs yet. Trigger an agent from the Command Bar (⌘K) or run a workflow to populate the audit trail.
            </div>
          )}
        </div>

        <div className="ai-banner" style={{ marginTop: 22 }}>
          <div className="ai-pill"><History style={{ width: 11, height: 11 }} /> Audit summary</div>
          <p style={{ fontSize: 12.5 }}>
            No agent runs to summarize yet. Every agent run is logged here with full prompts, tool calls, tokens, and artifacts.
          </p>
        </div>
      </div>
    </MarketingShell>
  );
}
